import * as React from 'react';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { isUserDataLoading, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { openLoginModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useNavigate } from 'react-router-dom';
import isEqual from 'react-fast-compare';
import modalTypes from '@/enums/modalTypes';

type EnsureLoggedInContainerProps = {
    children: React.ReactNode;
    loginModalOpen: boolean;
    navigate: ReturnType<typeof useNavigate>;
    openLoginModal: () => void;
    userDataLoading: boolean;
    userLoggedIn: boolean;
};

class EnsureLoggedInContainer extends React.Component<EnsureLoggedInContainerProps> {
    static defaultProps = {
        loginModalOpen: false,
    };

    componentDidMount() {
        this.timeout = setTimeout(() => {
            if (!this.props.userLoggedIn && !this.props.userDataLoading) {
                this.props.openLoginModal();
            }
        }, 1000);
    }

    shouldComponentUpdate = (nextProps) => !isEqual(this.props, nextProps);

    componentDidUpdate(prevProps) {
        const { loginModalOpen, navigate, openLoginModal, userDataLoading, userLoggedIn } = this.props;
        const { loginModalOpen: wasLoginModalOpen, userLoggedIn: wasUserLoggedIn } = prevProps;

        if (userDataLoading) {
            return;
        }

        if (
            (wasLoginModalOpen && !loginModalOpen && !userLoggedIn && !userDataLoading) ||
            (!loginModalOpen && !userLoggedIn && wasUserLoggedIn)
        ) {
            return navigate({ pathname: '/' });
        }

        if (!loginModalOpen && !userLoggedIn && !wasUserLoggedIn && !userDataLoading) {
            return openLoginModal();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);
    }

    timeout: any;

    render() {
        return this.props.userLoggedIn ? this.props.children : null;
    }
}

type EnsureLoggedInContainerWrapperProps = {
    children: React.ReactNode;
};

/**
 * This function allowed removing react-redux compose and redux-first-history from the class above without rewriting it's componentDidUpdate craziness
 */
const EnsureLoggedInContainerWrapper = ({ children }: EnsureLoggedInContainerWrapperProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const openModal = useAppSelector(getOpenModal);
    const loginModalOpen = openModal === modalTypes.LOGIN || openModal === modalTypes.COMPLETE_ACCOUNT_SIGNUP;
    const userDataLoading = useAppSelector(isUserDataLoading);

    return (
        <EnsureLoggedInContainer
            loginModalOpen={loginModalOpen}
            navigate={navigate}
            openLoginModal={() => dispatch(openLoginModal())}
            userDataLoading={userDataLoading}
            userLoggedIn={userLoggedIn}
        >
            {children}
        </EnsureLoggedInContainer>
    );
};

export default EnsureLoggedInContainerWrapper;
